// Read-this-screen-aloud for the engineer PWA (Session AFF). A hook that reads the meaningful text
// of a screen out through the Web Speech API for an engineer whose hands and eyes are on a lift, not
// the phone. PROGRESSIVE ENHANCEMENT ONLY: where the browser has no speechSynthesis the hook reports
// supported=false and the button is rendered disabled -- nothing ever throws, an engineer mid-job
// must never lose work to a speech quirk.
//
// It reuses speechAvailable() from src/lib/speech.ts for the feature check, but does its own
// utterance (rate 0.9, default voice) because it needs to track reading state via onend/onerror,
// which the fire-and-forget speak() helper does not expose.
import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { speechAvailable } from "./speech";

// The elements whose text we read: headings and body-copy blocks. Buttons, inputs and controls are
// deliberately excluded (their labels are actions, not content), as is anything hidden from the
// accessibility tree.
const READABLE_SELECTOR = "h1,h2,h3,h4,h5,h6,p,li,td,th,dt,dd,[role='status'],[role='alert']";

// An element whose text must never be read: a form control, an explicit button, or a node hidden
// from assistive tech. Its text is skipped even when it sits inside a readable block.
function isSkippableElement(el: Element): boolean {
  if (el.getAttribute("aria-hidden") === "true") return true;
  if (el.getAttribute("role") === "button") return true;
  const tag = el.tagName.toLowerCase();
  return tag === "button" || tag === "input" || tag === "textarea" || tag === "select";
}

// True when `node` (or any ancestor up to and including `stop`) must be skipped.
function skippedWithin(node: Node, stop: Element): boolean {
  let cur: Node | null = node;
  while (cur && cur instanceof Element) {
    if (isSkippableElement(cur)) return true;
    if (cur === stop) break;
    cur = cur.parentNode;
  }
  return false;
}

// The visible text of one readable block, with any control / hidden descendants removed and
// whitespace collapsed.
function blockText(block: Element): string {
  const walker = block.ownerDocument.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  const parts: string[] = [];
  let n: Node | null = walker.nextNode();
  while (n) {
    const value = n.nodeValue ?? "";
    if (value.trim() && n.parentElement && !skippedWithin(n.parentElement, block)) {
      parts.push(value);
    }
    n = walker.nextNode();
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

// Extract the readable text of a screen: each leaf-level readable block, in document order, joined
// with ". ". Only leaf blocks are read (a block that contains another readable block is skipped, so
// an <li> wrapping a <p> reads once, not twice), and any block inside a hidden / control subtree is
// dropped entirely.
export function extractReadableText(root: HTMLElement | null): string {
  if (!root) return "";
  const blocks = Array.from(root.querySelectorAll<HTMLElement>(READABLE_SELECTOR));
  const fragments: string[] = [];
  for (const block of blocks) {
    // Skip a block that itself sits inside a hidden / control subtree.
    if (skippedWithin(block, root)) continue;
    // Skip a container block: its readable children are read on their own turn.
    if (block.querySelector(READABLE_SELECTOR)) continue;
    const text = blockText(block);
    if (text) fragments.push(text);
  }
  return fragments.join(". ");
}

export interface ScreenReader {
  isReading: boolean;
  toggle: () => void;
  supported: boolean;
}

export function useScreenReader(contentRef: RefObject<HTMLElement | null>): ScreenReader {
  const supported = speechAvailable();
  const [isReading, setIsReading] = useState(false);
  // A ref mirror so the toggle callback (stable) reads the live reading state without re-binding.
  const readingRef = useRef(false);

  const setReading = useCallback((v: boolean) => {
    readingRef.current = v;
    setIsReading(v);
  }, []);

  const stop = useCallback(() => {
    try {
      window.speechSynthesis.cancel();
    } catch {
      /* silent: read-aloud is an enhancement, never a failure path */
    }
    setReading(false);
  }, [setReading]);

  const toggle = useCallback(() => {
    if (!supported) return;
    if (readingRef.current) {
      stop();
      return;
    }
    const text = extractReadableText(contentRef.current);
    if (!text) return; // nothing meaningful on screen -- do not start an empty utterance
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.9; // a touch slower than default: clearer over background plant noise
      u.onend = () => setReading(false);
      u.onerror = () => setReading(false);
      window.speechSynthesis.cancel(); // replace anything already speaking
      window.speechSynthesis.speak(u);
      setReading(true);
    } catch {
      setReading(false);
    }
  }, [supported, contentRef, stop, setReading]);

  // Stop speaking if the screen unmounts (navigating away): never keep reading a screen the engineer
  // has left.
  useEffect(() => {
    if (!supported) return;
    return () => {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* silent */
      }
    };
  }, [supported]);

  return { isReading, toggle, supported };
}
